import Vue from "vue";
import App from "./components/App.vue";
import router from "./router";
import store from "./store";
import { Route } from "vue-router";
import { DomainRegistry } from "./domain";
import { IConfiguration } from "./domain/configuration";
import { SessionStorageAccessor } from "./store/SessionStorageAccessor";
import { ILogger, LoggerFactory } from "./utilities/logger";

// Logger
LoggerFactory.initialize(); // 一旦デフォルトで初期化.
const logger = LoggerFactory.getLogger();

router.beforeEach(async (to, from, next) => {
  logger.info("ルーティング開始.");
  logger.info(to.fullPath);
  logger.debug("from " + from.fullPath);
  if (to.path === "/Error") {
    if (from.path === "/Error") {
      // replaceしてるので遷移前の画面で連続してエラーが発生した場合、エラー画面からの遷移になる模様.
      // 先のエラーを優先して表示する.
      logger.warn("Error画面表示中の更なるエラー.最初の表示優先でルーティング中断.");
      next(false);
      return;
    }
  } else {
/*
    if (!store.getters.configuration) {
      next({
        path: "/Error",
        query: {
          url: to.fullPath,
        }
      });
    }
*/
  }

  if (!store.getters.token && SessionStorageAccessor.token && SessionStorageAccessor.user) {
    store.commit("setToken", SessionStorageAccessor.token);
    store.commit("setUser", SessionStorageAccessor.user);
  }
  // ログイン画面へのリダイレクト.下記参考.
  // https://qiita.com/takatama/items/05e9fbc7199cde4caf60
  if (to.matched.some(record => record.meta.requiresAuth) && !isAlreadyLogined()) {
    logger.info("ログイン画面へリダイレクト.");
    const query = createRedirectQuery(to);
    next({
      path: "/Login",
      query,
      replace: true,
    });
    return;
  }
  if (to.matched.some(record => record.meta.requiresFolderAuth)
    && !isAlreadyFolderLogined(to.query.folderGuid as string)) {
      if (!(to.query.token as string)) {
        logger.info("フォルダ認証画面へリダイレクト.");
        const query = createRedirectQuery(to);
        query.guid = to.query.folderGuid as string; 
        next({
          path: "/Authentication",
          query,
          replace: true,
        });
        return;
      } else {
        // トークンからログイン情報を取得してstoreに登録
        const folderGuid = to.query.folderGuid as string;
        store.commit("setToken", to.query.token);
        const repository = DomainRegistry.folder.getFolderRepository();
        const result = await repository.getFolderAuthenticationInfo(folderGuid);
        // ゲストの場合もあるのでフォルダ操作可能なユーザーの場合だけstoreにセット
        if (result.user && result.user.id) { store.commit("setUser", result.user); }
        store.commit("setFolderGuid", folderGuid);
      }
  }

  logger.info("ルーティングされた画面へ.");
  next();
});

function isAlreadyLogined(): boolean {
  return !!store.getters.token
      && !!store.getters.user;
}

function isAlreadyFolderLogined(folderGuid: string): boolean {
  return !!store.getters.token
      && (store.getters.folderGuid === folderGuid);
}

function createRedirectQuery(to: Route): { [key: string]: string } {
  const query: { [key: string]: string } = {};
  const redirectUrl = to.fullPath;
  if (redirectUrl && redirectUrl !== "/") {
    query.redirect = redirectUrl;
  }
  return query;
}

Vue.config.productionTip = false;

import ElementUI from "element-ui";
// import "element-ui/lib/theme-chalk/index.css";
import "./forked-libs/element-ui/lib/theme/index.scss"; // カスタムテーマを使用
Vue.use(ElementUI);

// グローバルコンポーネント.
// import Tooltip from "./components/system/Tooltip.vue";
// Vue.component("tooltip", Tooltip);
import NotImplemented from "./components/system/NotImplementedComponent.vue";
Vue.component("notImplemented", NotImplemented);

// カスタムメソッド追加.
import { WindowOpener } from "./components/system/utilities/WindowOpener";
Vue.prototype.$openWindow = function(location: string, parameters: { [key: string]: string } = {}): void {
  WindowOpener.open(this.$route.path, location, parameters);
};
import { AsyncUtility } from "./utilities";
Vue.prototype.$wait = async function(isCompleted: () => boolean, pollingIntervalAsMsec: number = 100) {
  await AsyncUtility.wait(isCompleted, pollingIntervalAsMsec);
};
Vue.prototype.$sleep = async function(ms: number) {
  await AsyncUtility.sleep(ms);
};
Vue.prototype.$success = function(title: string, message: string, onClose: () => void = undefined): void {
  console.log(title);
  console.log(message);
  setTimeout(() => {
    this.$notify.success({
      title,
      message,
      position: "top-left",
      duration: 3000,
      onClose,
    });
  }, 1);
};
Vue.prototype.$successAsHTML = function(title: string, message: string): void {
  console.log(title);
  console.log(message);
  setTimeout(() => {
    this.$notify.success({
      title,
      message,
      dangerouslyUseHTMLString: true,
      position: "top-left",
      duration: 3000,
    });
  }, 1);
};
Vue.prototype.$info = function(title: string, message: string): void {
  console.info(title);
  console.info(message);
  setTimeout(() => {
    this.$notify.info({
      title,
      message,
      position: "top-left",
      duration: 0
    });
  }, 1);
};
Vue.prototype.$warn = function(title: string, message: string, onClose: () => void = undefined): void {
  console.warn(title);
  console.warn(message);
  setTimeout(() => {
    this.$notify({
      title,
      message,
      position: "top-left",
      duration: 5000,
      onClose,
      type: "warning",
    });
  }, 1);
};
Vue.prototype.$error = function(title: string, message: string, reason: any = undefined): void {
  console.error(title);
  console.error(message);
  if (reason) {
    console.error(reason);
  }
  setTimeout(() => {
    this.$notify.error({
      title,
      message,
      position: "top-left",
      duration: 0
    });
  }, 1);
};
Vue.prototype.$fatal = function(title: string, message: string, reason: any = undefined): void {
  console.error(title);
  console.error(message);
  if (reason) {
    console.error(reason);
  }
  this.$router.replace({
    path: "/Error",
    query: {
      url: location.href,
      message,
    },
  });
};
Vue.prototype.$notImplemented = function(message: string): void {
  console.error("未実装です。");
  setTimeout(() => {
    this.$notify.error({
      title: "未実装です。",
      message: !!message ? message : "実装したらこの通知の実装は削除してください。",
      position: "top-left",
      duration: 3000
    });
  }, 1);
};
Vue.prototype.$logger = LoggerFactory.getLogger();
Vue.prototype.$findAncestor = function(predicate: (s: Vue) => boolean): Vue {
  let ancestor = null as Vue;
  for (ancestor = this.$parent; (ancestor && !predicate(ancestor)); ancestor = ancestor.$parent) {
  }
  return ancestor;
};
Vue.prototype.$doWhenValid = function<T>(process: (i: T) => void, instance: T, ...instances: any[]): void {
  if (instance && Enumerable.from(instances).all(i => !!i)) {
    process(instance);
  }
};
Object.defineProperty(Vue.prototype, "$configuration", {
  get: function $configuration(): IConfiguration {
    return this.$store.getters.configuration as IConfiguration;
  }
});

import { LoadingServiceOptions, ElLoadingComponent } from "element-ui/types/loading";
import { ElMessageBox, ElMessageBoxShortcutMethod } from "element-ui/types/message-box";
import { ElNotification } from "element-ui/types/notification";
import { ElMessage } from "element-ui/types/message";
import Enumerable from "linq";
declare module "vue/types/vue" {
  interface Vue {
    $openWindow: (location: string, parameters?: { [key: string]: string }) => void;
    $wait: (isCompleted: () => boolean, pollingIntervalAsMsec?: number) => Promise<any>;
    $sleep: (ms: number) => Promise<void>;
    $success: (title: string, message: string, onClose?: () => void) => void;
    $successAsHTML: (title: string, message: string) => void;
    $info: (title: string, message: string) => void;
    $warn: (title: string, message: string, onClose?: () => void) => void;
    $error: (title: string, message: string, reason?: any) => void;
    $fatal: (title: string, message: string, reason?: any) => void;
    $notImplemented: (message?: string) => void;
    $logger: ILogger;
    $findAncestor: (predicate: (s: Vue) => boolean) => Vue;
    $doWhenValid: <T>(process: (i: T) => void, instance: T, ...instances: any[]) => void;
    $loading: (options: LoadingServiceOptions) => ElLoadingComponent;
    $msgbox: ElMessageBox;
    $alert: ElMessageBoxShortcutMethod;
    $confirm: ElMessageBoxShortcutMethod;
    $prompt: ElMessageBoxShortcutMethod;
    $notify: ElNotification;
    $message: ElMessage;
    $configuration: IConfiguration;
  }
}
initializeApplication()
.then(() => {
  /* eslint-disable no-new */
  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount("#app");
});

async function initializeApplication(): Promise<void> {
  try {
    await loadConfiguration();
  } catch (e) {
    logger.warn("設定の取得に失敗.");
  }
}

async function loadConfiguration(): Promise<void> {
  if (store.getters.configuration) {
    logger.info("既にstoreに格納されてる設定を利用します.");
    return;
  }
  const service = DomainRegistry.configuration.getConfigurationService();
  await service.load();
}
